import { useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash, Package, Camera, Copy } from "@phosphor-icons/react";
import AutocompleteInput from "@/components/AutocompleteInput";
import PhotoUploader from "@/components/PhotoUploader";

const SECURING_METHODS = [
  { id: "webbing_belts", label: "Webbing belts" },
  { id: "ratchet_tensioners", label: "Ratchet tensioners" },
  { id: "wooden_beams", label: "Wooden beams" },
  { id: "wooden_planks", label: "Wooden planks" },
  { id: "wedges", label: "Wedges" },
  { id: "tie_down_lashing", label: "Tie-down lashing" },
  { id: "top_over_lashing", label: "Top-over lashing" },
  { id: "blocking_with_wood", label: "Blocking with wood" },
  { id: "other", label: "Other" },
];

function FormField({ label, value, onChange, type = "text", placeholder = "", className = "", as = "input" }) {
  const Component = as === "textarea" ? Textarea : Input;
  return (
    <div className={className}>
      <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{label}</Label>
      <Component
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
  );
}

const EMPTY_CONTAINER = {
  container_number: "", container_type: "", commodity_description: "",
  gross_weight: "", serial_number: "", seal_number: "",
  tir_cord_seal_number: "", over_height: "", cargo_condition: "",
  container_condition: "", securing_method_description: "",
  securing_methods: [], lashing_details: "", blocking_details: "",
  observations: "", photos: [],
};

export default function LashingForm({ data, onChange }) {
  const containers = data.containers || [];

  const updateContainer = useCallback((index, field, value) => {
    const updated = [...(data.containers || [])];
    updated[index] = { ...updated[index], [field]: value };
    onChange("containers", updated);
  }, [data.containers, onChange]);

  const addContainer = () => {
    onChange("containers", [...containers, { ...EMPTY_CONTAINER }]);
  };

  const removeContainer = (index) => {
    onChange("containers", containers.filter((_, i) => i !== index));
  };

  const copyContainer = (index) => {
    const source = containers[index];
    const clone = { ...source, photos: [], container_number: "", serial_number: "", seal_number: "" };
    onChange("containers", [...containers, clone]);
  };

  const toggleSecuringMethod = (containerIndex, methodId) => {
    const c = containers[containerIndex];
    const methods = c.securing_methods || [];
    const updated = methods.includes(methodId)
      ? methods.filter(m => m !== methodId)
      : [...methods, methodId];
    updateContainer(containerIndex, "securing_methods", updated);
  };

  const handleContainerPhotoUploaded = useCallback((containerIndex, photoId) => {
    const updated = [...(data.containers || [])];
    const c = { ...updated[containerIndex] };
    c.photos = [...(c.photos || []), photoId];
    updated[containerIndex] = c;
    onChange("containers", updated);
  }, [data.containers, onChange]);

  const handleContainerPhotoRemoved = useCallback((containerIndex, photoId) => {
    const updated = [...(data.containers || [])];
    const c = { ...updated[containerIndex] };
    c.photos = (c.photos || []).filter(id => id !== photoId);
    updated[containerIndex] = c;
    onChange("containers", updated);
  }, [data.containers, onChange]);

  const handleContainerPhotoReorder = useCallback((containerIndex, reordered) => {
    const updated = [...(data.containers || [])];
    updated[containerIndex] = { ...updated[containerIndex], photos: reordered };
    onChange("containers", updated);
  }, [data.containers, onChange]);

  return (
    <div className="space-y-6" data-testid="lashing-form">
      {/* Header Section */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Header Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AutocompleteInput
            label="Applicant"
            value={data.applicant}
            onChange={v => onChange("applicant", v)}
            placeholder="Start typing to search..."
            endpoint="applicants"
            testId="applicant-input"
          />
          <FormField label="Our Reference Number" value={data.reference_number} onChange={v => onChange("reference_number", v)} placeholder="e.g., REF-2026-001" />
          <FormField label="Date of Issuance" value={data.date_of_issuance} onChange={v => onChange("date_of_issuance", v)} type="date" />
          <FormField label="Date(s) of Inspection" value={data.date_of_inspection} onChange={v => onChange("date_of_inspection", v)} type="date" />
          <FormField label="Place of Inspection" value={data.place_of_inspection} onChange={v => onChange("place_of_inspection", v)} placeholder="e.g., Port of Koper" />
          <AutocompleteInput
            label="Surveyor"
            value={data.surveyor}
            onChange={v => onChange("surveyor", v)}
            placeholder="Start typing to search..."
            endpoint="surveyors"
            testId="surveyor-input"
          />
        </div>
      </div>

      {/* Shipment Section */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Shipment Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Shipper" value={data.shipper} onChange={v => onChange("shipper", v)} />
          <FormField label="Notify Party" value={data.notify_party} onChange={v => onChange("notify_party", v)} />
          <FormField label="Port of Loading" value={data.port_of_loading} onChange={v => onChange("port_of_loading", v)} />
          <FormField label="Place of Delivery" value={data.place_of_delivery} onChange={v => onChange("place_of_delivery", v)} />
          <FormField label="Commodity Summary" value={data.commodity_summary} onChange={v => onChange("commodity_summary", v)} className="md:col-span-2" />
          <FormField label="Total Gross Weight" value={data.total_gross_weight} onChange={v => onChange("total_gross_weight", v)} placeholder="e.g., 24,500 kg" />
        </div>
      </div>

      {/* Containers */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Container Details ({containers.length})
          </h3>
          <Button
            type="button"
            onClick={addContainer}
            className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
            data-testid="add-container-btn"
          >
            <Plus size={16} weight="bold" /> Add Container
          </Button>
        </div>

        {containers.map((container, idx) => (
          <div key={idx} className="bg-white border border-slate-200 rounded-md p-6 relative" data-testid={`container-${idx}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Package size={20} className="text-blue-900" />
                <h4 className="font-bold text-slate-900">Container {idx + 1}</h4>
                {container.container_number && (
                  <span className="text-sm text-slate-500">({container.container_number})</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyContainer(idx)}
                  className="text-blue-700 hover:bg-blue-50"
                  title="Copy this container"
                  data-testid={`copy-container-${idx}`}
                >
                  <Copy size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeContainer(idx)}
                  className="text-red-500 hover:bg-red-50"
                  data-testid={`remove-container-${idx}`}
                >
                  <Trash size={16} />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Container Number" value={container.container_number} onChange={v => updateContainer(idx, "container_number", v)} placeholder="e.g., MSCU1234567" />
              <FormField label="Container Type" value={container.container_type} onChange={v => updateContainer(idx, "container_type", v)} placeholder="e.g., 40' HC" />
              <FormField label="Commodity Description" value={container.commodity_description} onChange={v => updateContainer(idx, "commodity_description", v)} className="md:col-span-2" />
              <FormField label="Gross Weight" value={container.gross_weight} onChange={v => updateContainer(idx, "gross_weight", v)} placeholder="e.g., 12,500 kg" />
              <FormField label="Serial Number" value={container.serial_number} onChange={v => updateContainer(idx, "serial_number", v)} />
              <FormField label="Seal Number" value={container.seal_number} onChange={v => updateContainer(idx, "seal_number", v)} />
              <FormField label="TIR Cord Seal Number" value={container.tir_cord_seal_number} onChange={v => updateContainer(idx, "tir_cord_seal_number", v)} />
              <FormField label="Over-height" value={container.over_height} onChange={v => updateContainer(idx, "over_height", v)} />
              <FormField label="Cargo Condition" value={container.cargo_condition} onChange={v => updateContainer(idx, "cargo_condition", v)} />
              <FormField label="Container Condition" value={container.container_condition} onChange={v => updateContainer(idx, "container_condition", v)} />
              <FormField label="Securing Method Description" value={container.securing_method_description} onChange={v => updateContainer(idx, "securing_method_description", v)} as="textarea" className="md:col-span-2" />
            </div>

            {/* Securing Methods Checkboxes */}
            <div className="mt-4">
              <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Securing Methods</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {SECURING_METHODS.map(method => (
                  <label key={method.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-slate-900">
                    <Checkbox
                      checked={(container.securing_methods || []).includes(method.id)}
                      onCheckedChange={() => toggleSecuringMethod(idx, method.id)}
                      data-testid={`securing-method-${method.id}-${idx}`}
                    />
                    {method.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <FormField label="Lashing Details" value={container.lashing_details} onChange={v => updateContainer(idx, "lashing_details", v)} as="textarea" />
              <FormField label="Blocking / Wooden Material Details" value={container.blocking_details} onChange={v => updateContainer(idx, "blocking_details", v)} as="textarea" />
              <FormField label="Observations" value={container.observations} onChange={v => updateContainer(idx, "observations", v)} as="textarea" className="md:col-span-2" />
            </div>

            {/* Container-level photos */}
            <div className="mt-5 pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <Camera size={16} className="text-blue-900" />
                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                  Container Photos ({(container.photos || []).length})
                </Label>
              </div>
              <PhotoUploader
                photos={container.photos || []}
                onPhotoUploaded={(pid) => handleContainerPhotoUploaded(idx, pid)}
                onRemovePhoto={(pid) => handleContainerPhotoRemoved(idx, pid)}
                onReorder={(reordered) => handleContainerPhotoReorder(idx, reordered)}
              />
            </div>
          </div>
        ))}

        {containers.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-400">
            <Package size={36} weight="light" className="mx-auto mb-2" />
            <p className="text-sm">No containers added yet. Click "Add Container" to begin.</p>
          </div>
        )}
      </div>

      {/* Findings */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Survey Findings & Observations
        </h3>
        <div className="space-y-4">
          <FormField label="Survey Findings" value={data.survey_findings} onChange={v => onChange("survey_findings", v)} as="textarea" />
          <FormField label="Observations" value={data.observations} onChange={v => onChange("observations", v)} as="textarea" />
          <FormField label="Disclaimer" value={data.disclaimer} onChange={v => onChange("disclaimer", v)} as="textarea" />
        </div>
      </div>
    </div>
  );
}
